import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, getDocs, where, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import QuizShareModal from './QuizShareModal';
import Select from 'react-select';
import { FiShare2 } from 'react-icons/fi';

function QuizGenerator() {
  const navigate = useNavigate();
  const [allQuestions, setAllQuestions] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableTopics, setAvailableTopics] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [generatedQuizId, setGeneratedQuizId] = useState(null);
  const [showShareNotification, setShowShareNotification] = useState(false);

  const [quizConfig, setQuizConfig] = useState({
    quizName: '',
    subjects: [],
    topics: [],
    difficulties: [],
    questionCount: 10,
    timeLimit: 15,
  });

  const LIMITS = {
    MIN_QUESTIONS: 1,
    MAX_QUESTIONS: 50,
    MIN_TIME: 5,
    MAX_TIME: 180,
    MIN_NAME_LENGTH: 3
  };

  const DIFFICULTY_LEVELS = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  useEffect(() => {
    fetchSubjectsTopicsAndQuestions();
  }, []);

  const fetchSubjectsTopicsAndQuestions = async () => {
    try {
      const questionsRef = collection(db, 'questions');
      const querySnapshot = await getDocs(questionsRef);
      
      const subjectTopicsMap = new Map();
      const questions = [];
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!subjectTopicsMap.has(data.subject)) {
          subjectTopicsMap.set(data.subject, new Set());
        }
        subjectTopicsMap.get(data.subject).add(data.topic);
        questions.push({ id: doc.id, ...data });
      });

      setAvailableSubjects(
        Array.from(subjectTopicsMap.keys())
          .filter(subject => subject !== "General")
          .map(s => ({ value: s, label: s }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
      setAvailableTopics(subjectTopicsMap);
      setAllQuestions(questions);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load subjects, topics, and questions");
      setLoading(false);
    }
  };

  const getTopicOptions = () => {
    const selectedSubjects = quizConfig.subjects.map(s => s.value);
    if (selectedSubjects.length === 0) return [];

    const topics = new Set();
    selectedSubjects.forEach(subject => {
      if (availableTopics.has(subject)) {
        availableTopics.get(subject).forEach(topic => topics.add(topic));
      }
    });

    return Array.from(topics).map(t => ({ value: t, label: t })).sort((a, b) => a.label.localeCompare(b.label));
  };

  const getFilteredQuestions = () => {
    return allQuestions.filter(q =>
      (quizConfig.subjects.length === 0 || quizConfig.subjects.some(s => s.value === q.subject)) &&
      (quizConfig.topics.length === 0 || quizConfig.topics.some(t => t.value === q.topic)) &&
      (quizConfig.difficulties.length === 0 || quizConfig.difficulties.some(d => d.value === q.difficulty))
    );
  };

  const filteredQuestions = getFilteredQuestions();
  const availableQuestionCount = filteredQuestions.length;

  const validateQuizConfig = () => {
    const errors = {};

    if (!quizConfig.quizName.trim()) {
      errors.quizName = 'Quiz name is required';
    } else if (quizConfig.quizName.length < LIMITS.MIN_NAME_LENGTH) {
      errors.quizName = `Quiz name must be at least ${LIMITS.MIN_NAME_LENGTH} characters`;
    }

    if (quizConfig.subjects.length === 0) {
      errors.subjects = 'Please select at least one subject';
    }

    if (quizConfig.questionCount < LIMITS.MIN_QUESTIONS || quizConfig.questionCount > LIMITS.MAX_QUESTIONS) {
      errors.questionCount = `Question count must be between ${LIMITS.MIN_QUESTIONS} and ${LIMITS.MAX_QUESTIONS}`;
    } else if (availableQuestionCount < quizConfig.questionCount) {
      errors.questionCount = `Only ${availableQuestionCount} questions available for selected criteria`;
    }

    if (quizConfig.timeLimit < LIMITS.MIN_TIME || quizConfig.timeLimit > LIMITS.MAX_TIME) {
      errors.timeLimit = `Time limit must be between ${LIMITS.MIN_TIME} and ${LIMITS.MAX_TIME} minutes`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateQuiz = async () => {
    if (!validateQuizConfig()) {
      return;
    }

    try {
      setLoading(true);
      
      const selectedQuestions = filteredQuestions
        .sort(() => Math.random() - 0.5)
        .slice(0, quizConfig.questionCount);

      const quizRef = await addDoc(collection(db, 'quizzes'), {
        ...quizConfig,
        subjects: quizConfig.subjects.map(s => s.value),
        topics: quizConfig.topics.map(t => t.value),
        difficulties: quizConfig.difficulties.map(d => d.value),
        questions: selectedQuestions,
        createdAt: new Date(),
        createdBy: auth.currentUser.uid,
        results: []
      });

      setGeneratedQuizId(quizRef.id);

      // --- Add notification for quiz generation ---
      try {
          await addDoc(collection(db, 'notifications'), {
            userId: auth.currentUser.uid, // Notify the user who generated the quiz
            title: 'Your Quiz is Ready!',
            message: `Your new quiz "**${quizConfig.quizName}**" has been successfully generated. You can now share it or take it yourself.`,
            type: 'quiz',
            read: false,
            dismissed: false,
            createdAt: serverTimestamp() // Use serverTimestamp for consistency
          });
          console.log('Quiz generation notification sent.');
      } catch (notificationError) {
          console.error('Error sending quiz generation notification:', notificationError);
      }
      // --- End notification logic ---

    } catch (error) {
      console.error("Error generating quiz:", error);
      setError("Failed to generate quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleTakeQuiz = () => {
    setGeneratedQuizId(null);
    navigate(`/take-quiz/${generatedQuizId}`);
  };

  // Share Notification Modal Component
  const ShareNotificationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
        <div className="flex items-center justify-center space-x-3">
          <FiShare2 className="text-[rgb(130,88,18)]" size={24} />
          <p className="text-lg font-medium text-gray-900">Quiz link copied to clipboard!</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-pulse text-gray-600">
          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <p>Loading quiz options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-6">Generate New Quiz</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Quiz Name</label>
          <input
            type="text"
            value={quizConfig.quizName}
            onChange={(e) => setQuizConfig(prev => ({ ...prev, quizName: e.target.value }))}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)] ${
              validationErrors.quizName ? 'border-red-500' : ''
            }`}
          />
          {validationErrors.quizName && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.quizName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Subject(s)</label>
          <Select
            isMulti
            options={availableSubjects}
            value={quizConfig.subjects}
            onChange={(selectedOptions) => setQuizConfig(prev => ({ ...prev, subjects: selectedOptions }))}
            classNamePrefix="react-select"
            className={`mt-1 block w-full rounded-md shadow-sm ${validationErrors.subjects ? 'border-red-500' : ''}`}
            styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? 'rgb(130,88,18)' : provided.borderColor,
                boxShadow: state.isFocused ? '0 0 0 1px rgb(130,88,18)' : provided.boxShadow,
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: 'rgb(130,88,18)',
                ':hover': { backgroundColor: 'rgb(130,88,18)', color: 'white' }
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? 'rgb(130,88,18)' : state.isFocused ? 'rgb(130,88,18, 0.1)' : undefined,
                color: state.isSelected ? 'white' : provided.color,
              }),
            }}
          />
          {validationErrors.subjects && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.subjects}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Topic(s)</label>
           <Select
            isMulti
            options={getTopicOptions()}
            value={quizConfig.topics}
            onChange={(selectedOptions) => setQuizConfig(prev => ({ ...prev, topics: selectedOptions }))}
            classNamePrefix="react-select"
            className={`mt-1 block w-full rounded-md shadow-sm ${validationErrors.topics ? 'border-red-500' : ''}`}
             isDisabled={quizConfig.subjects.length === 0}
             styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? 'rgb(130,88,18)' : provided.borderColor,
                boxShadow: state.isFocused ? '0 0 0 1px rgb(130,88,18)' : provided.boxShadow,
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: 'rgb(130,88,18)',
                ':hover': { backgroundColor: 'rgb(130,88,18)', color: 'white' }
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? 'rgb(130,88,18)' : state.isFocused ? 'rgb(130,88,18, 0.1)' : undefined,
                color: state.isSelected ? 'white' : provided.color,
              }),
            }}
          />
          {validationErrors.topics && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.topics}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Difficulty Level(s)</label>
           <Select
            isMulti
            options={DIFFICULTY_LEVELS}
            value={quizConfig.difficulties}
            onChange={(selectedOptions) => setQuizConfig(prev => ({ ...prev, difficulties: selectedOptions }))}
            classNamePrefix="react-select"
            className={`mt-1 block w-full rounded-md shadow-sm ${validationErrors.difficulties ? 'border-red-500' : ''}`}
            styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? 'rgb(130,88,18)' : provided.borderColor,
                boxShadow: state.isFocused ? '0 0 0 1px rgb(130,88,18)' : provided.boxShadow,
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: 'rgb(130,88,18)',
                ':hover': { backgroundColor: 'rgb(130,88,18)', color: 'white' }
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? 'rgb(130,88,18)' : state.isFocused ? 'rgb(130,88,18, 0.1)' : undefined,
                color: state.isSelected ? 'white' : provided.color,
              }),
            }}
          />
          {validationErrors.difficulties && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.difficulties}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Number of Questions</label>
          <input
            type="number"
            min={LIMITS.MIN_QUESTIONS}
            max={LIMITS.MAX_QUESTIONS}
            value={quizConfig.questionCount}
            onChange={(e) => setQuizConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) || 0 }))}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)] ${
              validationErrors.questionCount ? 'border-red-500' : ''
            }`}
          />
          {validationErrors.questionCount && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.questionCount}</p>
          )}
          {(quizConfig.subjects.length > 0 || quizConfig.topics.length > 0 || quizConfig.difficulties.length > 0) && (
            <div className="mt-1 text-sm text-gray-500">
              Available questions matching criteria: {availableQuestionCount}
            </div>
          )}
           {
               <div className="mt-2 text-sm">
                 <span className="font-medium">Distribution: </span>
                 {DIFFICULTY_LEVELS.filter(d => quizConfig.difficulties.some(sd => sd.value === d.value)).map(level => {
                   const count = filteredQuestions.filter(q => q.difficulty === level.value).length;
                   return (
                     <span key={level.value} className="mr-3">
                       {level.label}: {count}
                     </span>
                   );
                 })}
               </div>
            }
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Time Limit (minutes)</label>
          <input
            type="number"
            min={LIMITS.MIN_TIME}
            max={LIMITS.MAX_TIME}
            value={quizConfig.timeLimit}
            onChange={(e) => setQuizConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) || 0 }))}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)] ${
              validationErrors.timeLimit ? 'border-red-500' : ''
            }`}
          />
          {validationErrors.timeLimit && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.timeLimit}</p>
          )}
        </div>

        <button
          onClick={generateQuiz}
          disabled={loading || availableQuestionCount < quizConfig.questionCount}
          className="w-full bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)] focus:ring-offset-2 disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate Quiz'}
        </button>
      </div>

      {generatedQuizId && (
        <QuizShareModal
          quizId={generatedQuizId}
          onClose={() => setGeneratedQuizId(null)}
          onTakeQuiz={handleTakeQuiz}
          onShare={() => {
            setShowShareNotification(true);
            setTimeout(() => setShowShareNotification(false), 2000);
          }}
        />
      )}

      {/* Share Notification Modal */}
      {showShareNotification && <ShareNotificationModal />}
    </div>
  );
}

export default QuizGenerator; 